import axios from 'axios';
import { getPartnerToken, writeTabContext } from '@/services/auth/partnerSessionStorage';
import { OFFLINE_MODE_ENABLED } from '@/services/offline/offlineFeature';
import {
  deleteRecord,
  getRecord,
  listRecords,
  isLocalDbStorageExhausted,
  saveRecord,
  saveSyncState,
  getSyncState,
  addSyncLog,
} from '@/services/localDb';
import { getOrCreateDeviceId } from '@/services/localDb/device';
import { getOfflineSession, isOfflineSessionActive } from '@/services/offline/offlineUserAccess';
import { getPartnerSessionCache } from '@/services/offline/partnerSessionCache';
import { shouldUseOfflineMode } from '@/services/connectivity/connectivityService';

const OFFLINE_CACHE_PREFIX = 'offline_cache';
const BACKUP_PREFIX = 'restaurant_backup';
const OFFLINE_SECURITY_MIGRATION_STATE_KEY = 'offline_security:migration:v1';
const ACTIVE_RESTAURANT_STORAGE_KEY = 'ewu_tpv_active_restaurant_id';
const RESTAURANT_SCOPED_STORES = [
  'restaurant_config',
  'rooms',
  'tables',
  'categories',
  'products',
  'product_modifiers',
  'taxes',
  'waiters',
];

const OFFLINE_ALLOWED_RESTAURANT_CONFIG_FIELDS = [
  'accepts_payments',
  'address',
  'allow_facturation_bdp',
  'city',
  'country',
  'created_at',
  'currency',
  'delivery_name',
  'email',
  'id',
  'invoice_prefix',
  'is_counter',
  'is_delivery',
  'is_kiosk',
  'is_take_away',
  'language',
  'latitude',
  'logo',
  'longitude',
  'loyalty_active',
  'max_plates',
  'name',
  'nif',
  'profile_code',
  'open_kitchen',
  'pay_at_order',
  'phone',
  'postal_code',
  'primary_color',
  'province',
  'restaurant_id',
  'restaurant_logo',
  'secondary_color',
  'show_loading_overlays',
  'tax_included',
  'timezone',
  'updated_at',
  'vat_number',
];

const OFFLINE_ALLOWED_RESTAURANT_CUSTOMIZATION_FIELDS = [
  'logo',
  'primary_color',
  'secondary_color',
  'customer_background_color',
];

const OFFLINE_ALLOWED_PRINT_TEMPLATE_FIELDS = [
  'id',
  'restaurant_id',
  'template_key',
  'label',
  'channel',
  'template_format',
  'render_engine',
  'paper_width_mm',
  'page_height_mm',
  'html_template',
  'css_template',
  'data_schema_json',
  'sample_payload_json',
  'metadata_json',
  'version',
  'status',
  'is_active',
  'checksum',
  'published_at',
  'created_at',
  'updated_at',
];

let offlineSecurityMigrationPromise = null;

function apiBaseUrl() {
  return process.env.VUE_APP_API_URL;
}

function authToken() {
  return getPartnerToken();
}

function authHeaders() {
  const token = authToken();
  return token ? { Authorization: `Bearer ${token}` } : null;
}

function writeLastActiveRestaurantId(restaurantId) {
  try {
    writeTabContext(ACTIVE_RESTAURANT_STORAGE_KEY, restaurantId);
  } catch (error) {
    console.warn('Unable to persist active EWU restaurant id:', error);
  }
}

function nowIso() {
  return new Date().toISOString();
}

function cacheKey(restaurantId, key) {
  return `${OFFLINE_CACHE_PREFIX}:${restaurantId}:${key}`;
}

function backupKey(restaurantId, moduleId) {
  return `${BACKUP_PREFIX}:${restaurantId}:${moduleId}`;
}

function normalizeId(value) {
  if (value === undefined || value === null) return null;
  return String(value);
}

function getPartialFailureKeys(data = {}) {
  return new Set(
    (data.partial_errors || [])
      .map((entry) => normalizeId(entry?.key || entry?.endpoint))
      .filter(Boolean)
  );
}

function getRecordRestaurantId(record) {
  return normalizeId(record?.restaurant_id || record?.restaurant?.id || record?.restaurant);
}

function sanitizeRestaurantConfig(config = {}, restaurantId) {
  const sanitized = OFFLINE_ALLOWED_RESTAURANT_CONFIG_FIELDS.reduce((result, field) => {
    if (config[field] !== undefined) {
      result[field] = config[field];
    }
    return result;
  }, {});

  if (config.customization && typeof config.customization === 'object') {
    const customization = OFFLINE_ALLOWED_RESTAURANT_CUSTOMIZATION_FIELDS.reduce((result, field) => {
      if (config.customization[field] !== undefined) {
        result[field] = config.customization[field];
      }
      return result;
    }, {});

    if (Object.keys(customization).length) {
      sanitized.customization = customization;
    }
  }

  return {
    ...sanitized,
    restaurant_id: restaurantId,
    id: restaurantId,
  };
}

function sanitizePrintTemplateRecord(template = {}, restaurantId = null) {
  const sanitized = OFFLINE_ALLOWED_PRINT_TEMPLATE_FIELDS.reduce((result, field) => {
    if (template[field] !== undefined) {
      result[field] = template[field];
    }
    return result;
  }, {});

  const normalizedRestaurantId = normalizeId(template.restaurant_id || restaurantId);
  if (restaurantId && normalizedRestaurantId !== normalizeId(restaurantId)) {
    return null;
  }

  return {
    ...sanitized,
    id: normalizeId(template.id),
    restaurant_id: normalizedRestaurantId,
    template_key: String(template.template_key || '').trim(),
    label: String(template.label || '').trim(),
    channel: String(template.channel || 'shared').trim() || 'shared',
    template_format: String(template.template_format || 'html').trim() || 'html',
    render_engine: String(template.render_engine || 'mustache_v1').trim() || 'mustache_v1',
    paper_width_mm: Number(template.paper_width_mm || 80),
    page_height_mm: Number(template.page_height_mm || 297),
    html_template: String(template.html_template || ''),
    css_template: String(template.css_template || ''),
    data_schema_json: template.data_schema_json && typeof template.data_schema_json === 'object' ? template.data_schema_json : {},
    sample_payload_json: template.sample_payload_json && typeof template.sample_payload_json === 'object' ? template.sample_payload_json : {},
    metadata_json: template.metadata_json && typeof template.metadata_json === 'object' ? template.metadata_json : {},
    version: Number(template.version || 1),
    status: String(template.status || 'draft').trim() || 'draft',
    is_active: template.is_active !== false,
    checksum: String(template.checksum || ''),
    published_at: template.published_at || null,
    created_at: template.created_at || null,
    updated_at: template.updated_at || null,
  };
}

function sanitizePrintTemplateBackup(data = {}, restaurantId = null) {
  const rawTemplates = Array.isArray(data?.templates)
    ? data.templates
    : Array.isArray(data)
      ? data
      : [];

  const templates = rawTemplates
    .map((template) => sanitizePrintTemplateRecord(template, restaurantId))
    .filter(Boolean);

  const manifest = templates.map((template) => ({
    id: template.id,
    restaurant_id: template.restaurant_id,
    template_key: template.template_key,
    channel: template.channel,
    template_format: template.template_format,
    version: template.version,
    status: template.status,
    is_active: template.is_active,
    checksum: template.checksum,
    updated_at: template.updated_at || null,
    published_at: template.published_at || null,
  }));

  return {
    templates,
    manifest,
  };
}

function buildOfflineAuthorizedRestaurantContext() {
  if (!shouldUseOfflineMode() || !isOfflineSessionActive()) {
    return null;
  }

  const offlineSession = getOfflineSession();
  if (!offlineSession?.restaurant_id || !offlineSession?.user_id) {
    return null;
  }

  return {
    restaurant_id: normalizeId(offlineSession.restaurant_id),
    restaurant_name: offlineSession.restaurant_name || '',
    user_id: normalizeId(offlineSession.user_id),
    user_name: offlineSession.display_name || '',
    error: null,
    session_mode: 'offline_unlocked',
  };
}

function buildCachedPartnerRestaurantContext() {
  if (!authToken()) {
    return null;
  }

  const cachedPartner = getPartnerSessionCache();
  if (!cachedPartner?.restaurant_id || !cachedPartner?.id) {
    return null;
  }

  return {
    restaurant_id: normalizeId(cachedPartner.restaurant_id),
    restaurant_name: cachedPartner.restaurant_name || '',
    user_id: normalizeId(cachedPartner.id),
    user_name: cachedPartner.display_name || cachedPartner.username || '',
    error: null,
    session_mode: 'online_session_cached',
  };
}

export function sanitizeRestaurantBackupModuleData(moduleId, data = {}, restaurantId = null) {
  if (moduleId === 'restaurant_core') {
    const bootstrap = data?.pos_bootstrap || {};
    const catalog = data?.pos_catalog || {};
    return {
      ...(data || {}),
      floor_plans: data?.floor_plans || bootstrap.floor_plans || [],
      tables: data?.tables || bootstrap.tables || [],
      products: data?.products || catalog.plates || [],
      categories: data?.categories || catalog.categories || [],
      restaurant_config: sanitizeRestaurantConfig(data?.restaurant_config || {}, normalizeId(restaurantId)),
    };
  }

  if (moduleId === 'print_templates') {
    return sanitizePrintTemplateBackup(data, normalizeId(restaurantId));
  }

  return data || {};
}

function normalizeRestaurantRecord(context, config) {
  return {
    id: context.restaurant_id,
    name: config?.name || context.restaurant_name || '',
    address: config?.address || null,
    latitude: config?.latitude || null,
    longitude: config?.longitude || null,
    updated_at: nowIso(),
    local_cached_at: nowIso(),
  };
}

function normalizeRoom(room, restaurantId) {
  return {
    ...room,
    id: normalizeId(room.id),
    restaurant_id: restaurantId,
    updated_at: room.updated_at || nowIso(),
    local_cached_at: nowIso(),
  };
}

function normalizeTable(table, restaurantId) {
  const normalized = {
    ...table,
    id: normalizeId(table.id),
    restaurant_id: restaurantId,
    room_id: normalizeId(table.room_id || table.floor_plan_id),
    floor_plan_id: normalizeId(table.floor_plan_id),
    updated_at: table.updated_at || nowIso(),
    local_cached_at: nowIso(),
  };

  delete normalized.restaurant;
  return normalized;
}

function normalizeCategory(category, restaurantId) {
  return {
    ...category,
    id: normalizeId(category.id),
    restaurant_id: restaurantId,
    updated_at: category.updated_at || nowIso(),
    local_cached_at: nowIso(),
  };
}

function normalizeProduct(product, restaurantId) {
  return {
    ...product,
    id: normalizeId(product.id),
    restaurant_id: restaurantId,
    category_id: normalizeId(product.category_id || product.category?.id),
    available: product.available !== false,
    updated_at: product.updated_at || nowIso(),
    local_cached_at: nowIso(),
  };
}

function normalizeProductModifiers(product, restaurantId) {
  const steps = Array.isArray(product.steps) ? product.steps : [];
  return steps.map((step) => ({
    ...step,
    id: normalizeId(step.id),
    restaurant_id: restaurantId,
    product_id: normalizeId(product.id),
    updated_at: step.updated_at || nowIso(),
    local_cached_at: nowIso(),
  })).filter((step) => step.id);
}

function normalizeTaxes(products, restaurantId) {
  const taxValues = new Set();
  products.forEach((product) => {
    if (product.IVA !== undefined && product.IVA !== null) {
      taxValues.add(String(product.IVA));
    }
  });

  return Array.from(taxValues).map((taxValue) => ({
    id: `${restaurantId}:${taxValue}`,
    restaurant_id: restaurantId,
    value: Number(taxValue),
    updated_at: nowIso(),
    local_cached_at: nowIso(),
  }));
}

function normalizeWaiter(waiter, restaurantId) {
  return {
    id: normalizeId(waiter.id),
    restaurant_id: restaurantId,
    username: waiter.username,
    email: waiter.email || null,
    active: waiter.active !== false,
    tpv_permissions: waiter.tpv_permissions || [],
    updated_at: waiter.updated_at || nowIso(),
    local_cached_at: nowIso(),
  };
}

function isRecordInScope(restaurantId, record) {
  const recordRestaurantId = getRecordRestaurantId(record);
  return !recordRestaurantId || recordRestaurantId === restaurantId;
}

async function fetchScopedData(context) {
  const headers = authHeaders();
  if (!headers) {
    throw new Error('No hay token de partner activo para descargar datos offline.');
  }

  const baseUrl = apiBaseUrl();
  const [configRes, bootstrapRes, catalogRes, waitersRes] = await Promise.allSettled([
    axios.get(`${baseUrl}/v1/api/partner/get_restaurant_config`, { headers }),
    axios.get(`${baseUrl}/v1/api/app/pos/bootstrap`, { headers }),
    axios.get(`${baseUrl}/v1/api/app/pos/catalog`, { headers }),
    axios.get(`${baseUrl}/v1/api/app/GetCamareros`, { headers }),
  ]);

  const criticalFailures = [];
  const errors = [];
  const criticalResults = [
    ['restaurant_config', configRes],
    ['bootstrap', bootstrapRes],
    ['catalog', catalogRes],
  ];

  criticalResults.forEach(([key, result]) => {
    if (result.status !== 'rejected') return;
    criticalFailures.push(key);
    errors.push({
      key,
      message: result.reason?.message || String(result.reason),
    });
  });

  if (criticalFailures.length === criticalResults.length) {
    throw new Error(`No se pudo descargar ningun dataset base para modo offline: ${criticalFailures.join(', ')}`);
  }

  const configData = configRes.status === 'fulfilled' ? (configRes.value.data || {}) : {};
  const bootstrapData = bootstrapRes.status === 'fulfilled' ? (bootstrapRes.value.data || {}) : {};
  const catalogData = catalogRes.status === 'fulfilled' ? (catalogRes.value.data || {}) : {};

  return {
    restaurant: normalizeRestaurantRecord(context, configData),
    restaurant_config: sanitizeRestaurantConfig(configData, context.restaurant_id),
    products: catalogData.plates || [],
    categories: catalogData.categories || [],
    tables: bootstrapData.tables || [],
    rooms: bootstrapData.floor_plans || [],
    waiters: waitersRes.status === 'fulfilled' ? (waitersRes.value.data || []) : [],
    partial_errors: errors,
  };
}

async function upsertScopedRecords(storeName, restaurantId, records, normalizeRecord) {
  const seenIds = new Set();
  let savedCount = 0;
  let skippedCount = 0;

  for (const record of records) {
    if (!isRecordInScope(restaurantId, record)) {
      skippedCount += 1;
      await addSyncLog({
        level: 'error',
        message: `Skipped ${storeName} record outside active restaurant scope.`,
        entity_type: storeName,
        entity_id: normalizeId(record.id || record.local_id),
      });
      continue;
    }

    const normalized = normalizeRecord(record, restaurantId);
    if (!normalized?.id && !normalized?.local_id) {
      skippedCount += 1;
      continue;
    }

    const recordId = normalized.id || normalized.local_id;
    seenIds.add(recordId);
    await saveRecord(storeName, normalized);
    savedCount += 1;
  }

  await markMissingRecordsInactive(storeName, restaurantId, seenIds);
  return { savedCount, skippedCount };
}

async function markMissingRecordsInactive(storeName, restaurantId, seenIds) {
  const existingRecords = await listRecords(storeName, {
    indexName: 'restaurant_id',
    query: restaurantId,
  });

  await Promise.all(existingRecords.map(async (record) => {
    const recordId = record.id || record.local_id;
    if (!recordId || seenIds.has(recordId)) return;

    await saveRecord(storeName, {
      ...record,
      active: false,
      available: false,
      local_stale_at: nowIso(),
    });
  }));
}

async function setCacheState(restaurantId, key, value) {
  return saveSyncState(cacheKey(restaurantId, key), value);
}

async function getCacheState(restaurantId, key) {
  return getSyncState(cacheKey(restaurantId, key));
}

export async function getActiveRestaurantContext(options = {}) {
  void options;
  const offlineContext = buildOfflineAuthorizedRestaurantContext() || buildCachedPartnerRestaurantContext();
  const headers = authHeaders();
  if (offlineContext) {
    writeLastActiveRestaurantId(offlineContext.restaurant_id);
    return offlineContext;
  }
  if (!headers) {
    if (offlineContext) {
      writeLastActiveRestaurantId(offlineContext.restaurant_id);
      return offlineContext;
    }

    return {
      restaurant_id: null,
      restaurant_name: '',
      error: 'No hay sesión autorizada de partner activa.',
    };
  }

  try {
    const response = await axios.get(`${apiBaseUrl()}/v1/api/partner/me`, { headers });
    const restaurantId = normalizeId(response.data?.restaurant_id);

    if (!restaurantId) {
      return {
        restaurant_id: null,
        restaurant_name: '',
        error: 'No se pudo determinar el restaurante activo de la sesiÃ³n.',
      };
    }

    writeLastActiveRestaurantId(restaurantId);
    const cachedRestaurant = await getRecord('restaurants', restaurantId);
    return {
      restaurant_id: restaurantId,
      restaurant_name: cachedRestaurant?.name || response.data?.restaurant_name || '',
      user_id: normalizeId(response.data?.id),
      user_name: [response.data?.first_name, response.data?.last_name]
        .map((value) => String(value || '').trim())
        .filter(Boolean)
        .join(' ') || response.data?.username || response.data?.email || '',
      error: null,
      session_mode: 'online_validated',
    };
  } catch (error) {
    if (offlineContext) {
      writeLastActiveRestaurantId(offlineContext.restaurant_id);
      return {
        ...offlineContext,
        error: error?.message || null,
        is_fallback: true,
      };
    }

    return {
      restaurant_id: null,
      restaurant_name: '',
      error: error?.message || 'No se pudo leer la sesión activa autorizada.',
    };
  }
}

export async function persistPosOfflineCore(context, data = {}) {
  if (!OFFLINE_MODE_ENABLED) return null;
  const restaurantId = normalizeId(context?.restaurant_id);
  if (!restaurantId) {
    throw new Error('No se puede guardar la copia del TPV sin restaurant_id.');
  }

  const payload = {
    restaurant: normalizeRestaurantRecord(context, {
      name: context.restaurant_name || '',
    }),
    restaurant_config: sanitizeRestaurantConfig({
      id: restaurantId,
      name: context.restaurant_name || '',
    }, restaurantId),
    rooms: data.floor_plans || [],
    tables: data.tables || [],
    categories: data.categories || [],
    products: data.plates || data.products || [],
    waiters: [],
    partial_errors: [
      { key: 'restaurant_config', message: 'Se conserva la configuración local existente.' },
      { key: 'waiters', message: 'El bootstrap del TPV no incluye personal.' },
    ],
  };

  const result = await saveRestaurantOfflineData(restaurantId, payload);
  await setCacheState(restaurantId, 'last_success_at', nowIso());
  await setCacheState(restaurantId, 'last_attempt_at', nowIso());
  await setCacheState(restaurantId, 'status', 'ready');
  await setCacheState(restaurantId, 'error', '');
  await setCacheState(restaurantId, 'downloaded_by_device_id', getOrCreateDeviceId());
  return result;
}

export async function validateRestaurantScope(restaurantId, data) {
  if (!restaurantId) {
    return {
      valid: false,
      error: 'No hay restaurante activo para validar el alcance local.',
    };
  }

  const collections = [
    data.products || [],
    data.categories || [],
    data.tables || [],
    data.rooms || [],
    data.waiters || [],
  ];

  const outOfScope = collections.flat().filter((record) => !isRecordInScope(restaurantId, record));
  if (outOfScope.length > 0) {
    return {
      valid: false,
      error: `La descarga contiene ${outOfScope.length} registros de otro restaurante.`,
    };
  }

  return { valid: true, error: null };
}

export async function downloadRestaurantOfflineData(restaurantId = null) {
  if (!OFFLINE_MODE_ENABLED) throw new Error('La descarga offline está desactivada.');
  const context = restaurantId
    ? { restaurant_id: normalizeId(restaurantId), restaurant_name: '' }
    : await getActiveRestaurantContext();

  if (!context.restaurant_id) {
    throw new Error(context.error || 'No hay restaurante activo para descargar datos offline.');
  }

  await setCacheState(context.restaurant_id, 'last_attempt_at', nowIso());
  await setCacheState(context.restaurant_id, 'status', 'downloading');
  await setCacheState(context.restaurant_id, 'error', '');

  try {
    const data = await fetchScopedData(context);
    const validation = await validateRestaurantScope(context.restaurant_id, data);
    if (!validation.valid) {
      throw new Error(validation.error);
    }

    const result = await saveRestaurantOfflineData(context.restaurant_id, data);
    const cacheStatus = await getRestaurantOfflineCacheStatus(context.restaurant_id);
    const hasCoreData = Boolean(
      cacheStatus.counts?.products ||
      cacheStatus.counts?.categories ||
      cacheStatus.counts?.rooms ||
      cacheStatus.counts?.tables
    );
    if (!hasCoreData) {
      throw new Error('La copia local no contiene datos base del restaurante tras guardarse en IndexedDB.');
    }

    await setCacheState(context.restaurant_id, 'last_success_at', nowIso());
    await setCacheState(context.restaurant_id, 'status', data.partial_errors?.length ? 'partial' : 'ready');
    await setCacheState(context.restaurant_id, 'downloaded_by_device_id', getOrCreateDeviceId());
    await setCacheState(context.restaurant_id, 'partial_errors', data.partial_errors || []);

    return {
      ...result,
      persisted_counts: cacheStatus.counts,
    };
  } catch (error) {
    await setCacheState(context.restaurant_id, 'status', 'error');
    await setCacheState(context.restaurant_id, 'error', error?.message || String(error));
    await addSyncLog({
      level: 'error',
      message: 'Restaurant offline data download failed.',
      entity_type: 'offline_cache',
      entity_id: context.restaurant_id,
      details: error?.message || String(error),
    });
    throw error;
  }
}

export async function saveRestaurantOfflineData(restaurantId, data) {
  if (!OFFLINE_MODE_ENABLED) throw new Error('La copia offline está desactivada.');
  if (!restaurantId) {
    throw new Error('No se pueden guardar datos offline sin restaurant_id.');
  }

  const currentTime = nowIso();
  const partialFailureKeys = getPartialFailureKeys(data);
  const sanitizedRestaurantConfig = sanitizeRestaurantConfig(data.restaurant_config || {}, restaurantId);
  const normalizedRestaurant = {
    ...data.restaurant,
    id: restaurantId,
    local_cached_at: currentTime,
  };
  const shouldPreserveRestaurantConfig = partialFailureKeys.has('restaurant_config');
  if (!shouldPreserveRestaurantConfig || !(await getRecord('restaurant_config', restaurantId))) {
    await saveRecord('restaurants', normalizedRestaurant);
    await saveRecord('restaurant_config', {
      ...sanitizedRestaurantConfig,
      restaurant_id: restaurantId,
      updated_at: sanitizedRestaurantConfig.updated_at || currentTime,
      local_cached_at: currentTime,
    });
  }

  const emptyResult = { savedCount: 0, skippedCount: 0 };
  const roomsResult = partialFailureKeys.has('rooms')
    ? emptyResult
    : await upsertScopedRecords('rooms', restaurantId, data.rooms || [], normalizeRoom);
  const tablesResult = partialFailureKeys.has('tables')
    ? emptyResult
    : await upsertScopedRecords('tables', restaurantId, data.tables || [], normalizeTable);
  const categoriesResult = partialFailureKeys.has('categories')
    ? emptyResult
    : await upsertScopedRecords('categories', restaurantId, data.categories || [], normalizeCategory);
  const productsResult = partialFailureKeys.has('products')
    ? emptyResult
    : await upsertScopedRecords('products', restaurantId, data.products || [], normalizeProduct);
  const waitersResult = partialFailureKeys.has('waiters')
    ? emptyResult
    : await upsertScopedRecords('waiters', restaurantId, data.waiters || [], normalizeWaiter);

  const modifiers = partialFailureKeys.has('products')
    ? []
    : (data.products || []).flatMap((product) => normalizeProductModifiers(product, restaurantId));
  const modifiersResult = partialFailureKeys.has('products')
    ? emptyResult
    : await upsertScopedRecords('product_modifiers', restaurantId, modifiers, (record) => record);

  const taxes = partialFailureKeys.has('products') ? [] : normalizeTaxes(data.products || [], restaurantId);
  if (!partialFailureKeys.has('products')) {
    await Promise.all(taxes.map((tax) => saveRecord('taxes', tax)));
  }

  return {
    restaurant_id: restaurantId,
    saved_at: currentTime,
    partial_errors: data.partial_errors || [],
    preserved_datasets: [...partialFailureKeys],
    counts: {
      rooms: roomsResult.savedCount,
      tables: tablesResult.savedCount,
      categories: categoriesResult.savedCount,
      products: productsResult.savedCount,
      product_modifiers: modifiersResult.savedCount,
      taxes: taxes.length,
      waiters: waitersResult.savedCount,
    },
    skipped: {
      rooms: roomsResult.skippedCount,
      tables: tablesResult.skippedCount,
      categories: categoriesResult.skippedCount,
      products: productsResult.skippedCount,
      product_modifiers: modifiersResult.skippedCount,
      waiters: waitersResult.skippedCount,
    },
  };
}

export async function restoreRestaurantOfflineDataFromBackupSnapshot(restaurantId) {
  if (!restaurantId) return null;

  const snapshot = await getSyncState(backupKey(restaurantId, 'restaurant_core'));
  const snapshotData = snapshot?.data;
  if (!snapshotData) return null;

  const restaurantConfig = snapshotData.restaurant_config || {};
  const restored = await saveRestaurantOfflineData(restaurantId, {
    restaurant: normalizeRestaurantRecord({
      restaurant_id: restaurantId,
      restaurant_name: restaurantConfig?.name || '',
    }, restaurantConfig),
    restaurant_config: sanitizeRestaurantConfig(restaurantConfig, restaurantId),
    rooms: snapshotData.floor_plans || snapshotData.rooms || [],
    tables: snapshotData.tables?.tables || snapshotData.tables || [],
    categories: snapshotData.categories || [],
    products: snapshotData.products || [],
    waiters: snapshotData.waiters || [],
    partial_errors: snapshot.errors || [],
  });

  await setCacheState(restaurantId, 'last_success_at', snapshot.saved_at || nowIso());
  await setCacheState(restaurantId, 'status', 'restored_from_backup');
  return restored;
}

export async function ensureRestaurantOfflineCoreAvailable(restaurantId) {
  if (!restaurantId) return false;

  const [products, tables, categories, rooms] = await Promise.all([
    getLocalProducts(restaurantId),
    getLocalTables(restaurantId),
    getLocalCategories(restaurantId),
    getLocalRooms(restaurantId),
  ]);

  if (products.length || tables.length || categories.length || rooms.length) {
    return true;
  }

  if (isLocalDbStorageExhausted()) {
    return false;
  }

  const restored = await restoreRestaurantOfflineDataFromBackupSnapshot(restaurantId);
  return Boolean(restored);
}

export async function getRestaurantOfflineCacheStatus(restaurantId) {
  if (!restaurantId) {
    return {
      restaurant_id: null,
      available: false,
      error: 'No hay restaurante activo.',
      counts: {},
    };
  }

  const [restaurant, lastSuccessAt, lastAttemptAt, status, error, deviceId] = await Promise.all([
    getRecord('restaurants', restaurantId),
    getCacheState(restaurantId, 'last_success_at'),
    getCacheState(restaurantId, 'last_attempt_at'),
    getCacheState(restaurantId, 'status'),
    getCacheState(restaurantId, 'error'),
    getCacheState(restaurantId, 'downloaded_by_device_id'),
  ]);

  const [products, categories, rooms, tables, pendingActions] = await Promise.all([
    getLocalProducts(restaurantId),
    getLocalCategories(restaurantId),
    getLocalRooms(restaurantId),
    getLocalTables(restaurantId),
    listRecords('pending_actions'),
  ]);

  return {
    restaurant_id: restaurantId,
    restaurant_name: restaurant?.name || '',
    available: Boolean(lastSuccessAt && (products.length || categories.length || rooms.length || tables.length)),
    last_success_at: lastSuccessAt,
    last_attempt_at: lastAttemptAt,
    status: status || 'empty',
    error: error || '',
    downloaded_by_device_id: deviceId,
    counts: {
      products: products.length,
      categories: categories.length,
      rooms: rooms.length,
      tables: tables.length,
      pending_actions: pendingActions.filter((action) =>
        action.restaurant_id === restaurantId && !['completed', 'cancelled'].includes(action.status)
      ).length,
    },
  };
}

export async function hasRestaurantOfflineData(restaurantId) {
  const status = await getRestaurantOfflineCacheStatus(restaurantId);
  return status.available;
}

export async function getLocalRestaurantConfig(restaurantId) {
  if (!restaurantId) return null;
  return getRecord('restaurant_config', restaurantId);
}

async function migrateStoredRestaurantConfigs() {
  const records = await listRecords('restaurant_config');
  let migrated = 0;

  await Promise.all(records.map(async (record) => {
    const restaurantId = normalizeId(record?.restaurant_id || record?.id);
    if (!restaurantId) return;

    const nextRecord = {
      ...record,
      ...sanitizeRestaurantConfig(record, restaurantId),
      restaurant_id: restaurantId,
      local_cached_at: record.local_cached_at || nowIso(),
      updated_at: record.updated_at || nowIso(),
    };

    if (JSON.stringify(record) === JSON.stringify(nextRecord)) {
      return;
    }

    await saveRecord('restaurant_config', nextRecord);
    migrated += 1;
  }));

  return migrated;
}

async function migrateStoredRestaurantBackupSnapshots() {
  const syncStateRecords = await listRecords('sync_state');
  let migrated = 0;

  await Promise.all(syncStateRecords.map(async (record) => {
    if (!record?.key?.startsWith(`${BACKUP_PREFIX}:`)) return;

    const snapshot = record.value;
    if (!snapshot?.module_id || !snapshot?.data) return;

    const restaurantId = normalizeId(snapshot.restaurant_id || record.key.split(':')[1]);
    const nextSnapshot = {
      ...snapshot,
      restaurant_id: restaurantId,
      data: sanitizeRestaurantBackupModuleData(snapshot.module_id, snapshot.data, restaurantId),
    };

    if (JSON.stringify(snapshot) === JSON.stringify(nextSnapshot)) {
      return;
    }

    await saveSyncState(record.key, nextSnapshot);
    migrated += 1;
  }));

  return migrated;
}

export async function runOfflineSecurityMigration() {
  if (offlineSecurityMigrationPromise) {
    return offlineSecurityMigrationPromise;
  }

  offlineSecurityMigrationPromise = (async () => {
    const previous = await getSyncState(OFFLINE_SECURITY_MIGRATION_STATE_KEY);
    if (previous?.completed) {
      return previous;
    }

    const [restaurantConfigsSanitized, backupSnapshotsSanitized] = await Promise.all([
      migrateStoredRestaurantConfigs(),
      migrateStoredRestaurantBackupSnapshots(),
    ]);

    const summary = {
      completed: true,
      migrated_at: nowIso(),
      restaurant_configs_sanitized: restaurantConfigsSanitized,
      backup_snapshots_sanitized: backupSnapshotsSanitized,
    };

    await saveSyncState(OFFLINE_SECURITY_MIGRATION_STATE_KEY, summary);
    return summary;
  })();

  try {
    return await offlineSecurityMigrationPromise;
  } finally {
    offlineSecurityMigrationPromise = null;
  }
}

export async function getLocalRooms(restaurantId) {
  if (!restaurantId) return [];
  return listRecords('rooms', { indexName: 'restaurant_id', query: restaurantId });
}

export async function getLocalTables(restaurantId) {
  if (!restaurantId) return [];
  return listRecords('tables', { indexName: 'restaurant_id', query: restaurantId });
}

export async function getLocalCategories(restaurantId) {
  if (!restaurantId) return [];
  return listRecords('categories', { indexName: 'restaurant_id', query: restaurantId });
}

export async function getLocalProducts(restaurantId) {
  if (!restaurantId) return [];
  return listRecords('products', { indexName: 'restaurant_id', query: restaurantId });
}

export async function clearRestaurantLocalData(restaurantId) {
  if (!restaurantId) return false;

  await Promise.all(RESTAURANT_SCOPED_STORES.map(async (storeName) => {
    const records = await listRecords(storeName, {
      indexName: 'restaurant_id',
      query: restaurantId,
    });

    await Promise.all(records.map((record) => {
      const key = record.local_id || record.id || record.restaurant_id;
      return deleteRecord(storeName, key);
    }));
  }));

  await deleteRecord('restaurants', restaurantId);

  const syncStateRecords = await listRecords('sync_state');
  await Promise.all(syncStateRecords
    .filter((record) => record.key?.startsWith(`${OFFLINE_CACHE_PREFIX}:${restaurantId}:`))
    .map((record) => deleteRecord('sync_state', record.key)));

  return true;
}
